import React from 'react';
import { Link } from 'react-router-dom';
import './Privacy.css';

export default function Privacy() {
  return (
    <div className="privacy-page">
      <header className="privacy-header">
        <Link to="/" className="back-button">← SafeHer</Link>
        <h1>Privacy Policy</h1>
        <p>How we handle your data — and why privacy is our foundation</p>
      </header>

      <div className="privacy-content">

        <h2>1. Who We Are</h2>
        <p>SafeHer ("we", "our", "the app") is a community information-sharing platform built for women. We operate the mobile app (iOS and Android) and the web application at <a href="https://safeher.xyz">safeher.xyz</a>. Our contact email is <a href="mailto:support@safeher.xyz">support@safeher.xyz</a>.</p>
        <p><strong>SafeHer is not an emergency service.</strong> If you are in immediate danger, please contact your local emergency services. SafeHer exists to help communities share information about patterns and situations early — it is not a substitute for emergency response.</p>

        <h2>2. What Data We Collect</h2>
        <p>We collect the minimum data necessary to operate the app:</p>
        <ul>
          <li><strong>Account data:</strong> Your email address and a bcrypt-hashed password. We never store your password in plain text.</li>
          <li><strong>Profile data (optional):</strong> First name, last name, and a city or neighbourhood for context. You can leave these blank.</li>
          <li><strong>Reports:</strong> Incident descriptions, severity level, and the map coordinates of the location you pin. Reports are submitted anonymously by default — your name and account are never attached to a report visible to other users.</li>
          <li><strong>Location data:</strong> Only used in-session for map centering and proximity alerts. We do not store your device location history.</li>
          <li><strong>Moderator requests:</strong> If you apply to become a moderator, we collect your full name (for admin review only, never shown publicly) and the city or neighbourhood you wish to cover.</li>
        </ul>

        <h2>3. What We Do Not Collect</h2>
        <ul>
          <li>We do not collect advertising identifiers (IDFA/GAID).</li>
          <li>We do not track you across other apps or websites.</li>
          <li>We do not sell, rent, or share your data with advertisers or data brokers.</li>
          <li>We do not use third-party analytics SDKs (e.g. Firebase Analytics, Mixpanel).</li>
        </ul>

        <h2>4. How We Use Your Data</h2>
        <ul>
          <li>To authenticate your account and keep your session secure.</li>
          <li>To display community safety reports on the map.</li>
          <li>To send transactional emails (email verification, password reset). No marketing emails are sent without your explicit opt-in.</li>
          <li>To allow moderators to respond to reports you submit — always via pseudonymous identifiers, never your real name or email.</li>
        </ul>

        <h2>5. Anonymity by Design</h2>
        <p>SafeHer is built with anonymity as a structural guarantee, not a policy choice. When you submit a report:</p>
        <ul>
          <li>Your report is tagged with a session-derived pseudonymous ID — not your name or email.</li>
          <li>Other users, moderators, and admins never see who submitted a report unless you explicitly choose to make it non-anonymous.</li>
          <li>Moderator-to-reporter messages are delivered via your in-app inbox without exposing your contact details to the moderator.</li>
        </ul>

        <h2>6. Data Storage and Security</h2>
        <p>Your data is stored on secure cloud infrastructure in the United States. Passwords are hashed using industry-standard one-way encryption. Authentication tokens expire after 7 days. All data is transmitted over HTTPS/TLS.</p>

        <h2>7. Your Rights</h2>
        <ul>
          <li><strong>Access:</strong> You can view all reports you have submitted from your profile page.</li>
          <li><strong>Deletion:</strong> You can delete your account at any time from the app (Profile → Delete Account) or the website (Profile tab → Delete Account). Your account and personal data are removed immediately. Community data you contributed (reports, comments, upvotes) is anonymised by default — you may request full deletion of this data at the time of account deletion. You can also email <a href="mailto:support@safeher.xyz">support@safeher.xyz</a> to request deletion.</li>
          <li><strong>Correction:</strong> You can update your profile details from the settings screen.</li>
          <li><strong>Portability:</strong> Contact us to request a copy of your data in JSON format.</li>
        </ul>

        <h2>7a. Data Deletion Timeline</h2>
        <p>When you delete your account, your personal data (email, password, profile) is removed immediately. If you request deletion of community data (reports, comments, upvotes), those requests are fulfilled within 90 days. During this period, the data remains anonymised with no link to your identity. Anonymised data that you did not request deletion of may be retained indefinitely as part of the community safety record.</p>

        <h2>8. Children's Privacy</h2>
        <p>SafeHer is not directed at children under the age of 13. We do not knowingly collect personal data from children. If you believe a child has submitted data, contact us and we will delete it promptly.</p>

        <h2>9. Changes to This Policy</h2>
        <p>We may update this policy from time to time. We will notify registered users of material changes via email. The date at the bottom of this page always reflects the most recent version.</p>

        <h2>10. Contact</h2>
        <p>For any privacy questions or data requests, contact us at:</p>
        <div className="privacy-contact">
          <a href="mailto:support@safeher.xyz">support@safeher.xyz</a>
        </div>

        <div className="privacy-last-updated">
          Last updated: May 2026 · SafeHer · <a href="https://safeher.xyz">safeher.xyz</a>
        </div>
      </div>
    </div>
  );
}
